import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const HEARTBEAT_INTERVAL = 20_000; // 20s
const ONLINE_THRESHOLD = 60_000; // 60s — consider offline after this

type UseOnlineStatusOptions = {
  enableHeartbeat?: boolean;
  observeOnlineUsers?: boolean;
};

export type OnlineStatusData = {
  onlineUsers: Set<string>;
  lastSeenMap: Record<string, string>;
};

export const useOnlineStatus = ({
  enableHeartbeat = true,
  observeOnlineUsers = true,
}: UseOnlineStatusOptions = {}): OnlineStatusData => {
  const { user } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const [lastSeenMap, setLastSeenMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!user?.id) {
      setOnlineUsers(new Set());
      setLastSeenMap({});
      return;
    }

    const db = supabase as any;

    const fetchOnlineUsers = async () => {
      if (!observeOnlineUsers) return;
      const { data } = await db
        .from("user_online_status")
        .select("user_id, is_online, last_seen");

      if (!data) return;

      const threshold = new Date(Date.now() - ONLINE_THRESHOLD).toISOString();
      const ids = (data as any[])
        .filter((row: any) => row.is_online && row.last_seen > threshold)
        .map((row: any) => row.user_id);
      setOnlineUsers(new Set(ids));

      const seenMap: Record<string, string> = {};
      for (const row of data as any[]) {
        if (row.last_seen) seenMap[row.user_id] = row.last_seen;
      }
      setLastSeenMap(seenMap);
    };

    const heartbeat = async () => {
      await db.from("user_online_status").upsert(
        {
          user_id: user.id,
          is_online: true,
          last_seen: new Date().toISOString(),
        },
        { onConflict: "user_id" },
      );
    };

    let isPageUnloading = false;

    const handleBeforeUnload = () => {
      isPageUnloading = true;
    };

    if (enableHeartbeat) heartbeat();
    if (observeOnlineUsers) fetchOnlineUsers();

    const interval = setInterval(() => {
      if (enableHeartbeat) heartbeat();
      if (observeOnlineUsers) fetchOnlineUsers();
    }, HEARTBEAT_INTERVAL);

    const subscription = observeOnlineUsers
      ? supabase
          .channel("online-status")
          .on("postgres_changes", { event: "*", schema: "public", table: "user_online_status" }, () => {
            fetchOnlineUsers();
          })
          .subscribe()
      : null;

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (enableHeartbeat && !isPageUnloading) {
        db.from("user_online_status")
          .update({ is_online: false, last_seen: new Date().toISOString() })
          .eq("user_id", user.id)
          .then(() => {});
      }

      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [enableHeartbeat, observeOnlineUsers, user?.id]);

  return { onlineUsers, lastSeenMap };
};
